/**
 * Analytics Service for handling teacher analytics API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured analytics service
 * @param {Object} config - Configuration options
 * @param {Function} config.onError - Global error handler
 * @returns {Object} The analytics service API
 */
export function createAnalyticsService(config = {}) {
  // Get configuration functions
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Analytics service', error));

  /**
   * Get class diagnostic report for a specific course
   * @param {string} courseId - The course ID
   * @returns {Promise<Object>} The diagnostic report data
   */
  const getClassDiagnosticReport = async (courseId) => {
    if (!courseId) {
      throw new Error('Course ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.classDiagnosticReport(courseId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get analytics report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get eligible subjects with disciplines that have activities
   * @returns {Promise<Object>} The subjects data with nested disciplines
   */
  const getEligibleSubjects = async () => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.eligibleSubjects(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get eligible subjects: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get flagged messages for inappropriate use analytics
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {number} params.limit - Max messages to return
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<Object>} The flagged messages data with pagination
   */
  const getFlaggedMessages = async ({ courseIds, startDate, endDate, limit = 3, offset = 0 } = {}) => {
    try {
      const urls = getApiUrls();
      const params = new URLSearchParams();

      const courseIdParams = courseIds?.length
        ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&')
        : '';

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.flaggedMessages()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get flagged messages: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get topic detail (student breakdown) for a specific topic
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {string} params.topicId - The topic ID to get detail for
   * @returns {Promise<Object>} The topic detail data with user breakdown
   */
  const getTopicDetail = async ({ courseIds, startDate, endDate, topicId } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length
        ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&')
        : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (topicId) params.append('topicId', topicId);

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.topicDetail()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get topic detail: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get topic ranking for analytics
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {number} params.limit - Max topics to return
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<Object>} The topic ranking data
   */
  const getTopicRanking = async ({ courseIds, startDate, endDate, limit = 10, offset = 0 } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length
        ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&')
        : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.topicRanking()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get topic ranking: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  // Return the service API
  return {
    getClassDiagnosticReport,
    getEligibleSubjects,
    getFlaggedMessages,
    getTopicDetail,
    getTopicRanking,
  };
}

// Default export with default configuration
export default createAnalyticsService();
